import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ELLaiLogo from '../components/ELLaiLogo';
import { api } from '../services/api';
import '../styles/lx.css';
import './LoginPage.css';

function LoginPage({ onLogin }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for invite token in URL
  const inviteToken = searchParams.get('invite');

  // Get the return URL from location state (where user tried to go before login)
  const returnUrl = location.state?.from || null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If there's an invite token, set the return URL to the invitation page
  useEffect(() => {
    // Store invite token in session for after login
    if (inviteToken) {
      sessionStorage.setItem('pendingInvite', inviteToken);
    }
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/api/auth/login', { email, password });

      const data = await response.json();

      if (response.ok) {
        onLogin(data.token, data.user);

        // Check for pending invitation
        const pendingInvite = sessionStorage.getItem('pendingInvite');
        if (pendingInvite) {
          sessionStorage.removeItem('pendingInvite');
          navigate(`/invite/${pendingInvite}`, { replace: true });
        } else if (returnUrl) {
          // If there's a return URL, navigate there after login
          navigate(returnUrl, { replace: true });
        }
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page lx-page">
      <div className="login-container">
        {/* Logo / Brand — growth film into the mark */}
        <div className="brand">
          <ELLaiLogo size="medium" animated />
        </div>

        {/* Login panel */}
        <div className="login-panel">
          <h2>Welcome back</h2>
          <p className="login-subtitle">Sign in to continue your journey</p>

          {error && <div className="login-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="lx-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>

            <div className="lx-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              className="lx-btn lx-btn--full"
              disabled={loading}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="login-forgot">
            <Link to="/forgot-password">Forgot your password?</Link>
          </p>

          <div className="login-divider"><span>or</span></div>

          <Link to="/signup" className="lx-btn lx-btn--ghost lx-btn--full">
            Create Account
          </Link>
        </div>

        {/* Footer */}
        <p className="login-footer-text">
          ELLai helps families preserve and share the voices, memories,
          and wisdom of loved ones across generations.
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
